import {
  getExportReportUrl,
  useGetReport,
  useListProperties,
  type GetReportParams,
  type ReportColumnOut,
  type ReportName,
  type ReportRowOut,
} from '@myrentalmate/api-client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { TablePager, useTablePage } from '../../components/TablePager';
import { okData, okItems } from '../../lib/apiData';
import { formatDate, formatMoney, todayIso } from '../../lib/format';

type Filter = 'dates' | 'asOf' | 'property' | 'basis' | 'account' | 'year';

const REPORTS: { name: ReportName; label: string; about: string; filters: Filter[] }[] = [
  {
    name: 'income_statement',
    label: 'Income Statement',
    about: 'Income and expenses for a period.',
    filters: ['dates', 'property', 'basis'],
  },
  {
    name: 'balance_sheet',
    label: 'Balance Sheet',
    about: 'What the business has and owes on a date.',
    filters: ['asOf'],
  },
  {
    name: 'cash_flow',
    label: 'Cash Flow',
    about: 'Money in and out of your cash accounts.',
    filters: ['dates'],
  },
  {
    name: 'rent_roll',
    label: 'Rent Roll',
    about: 'Every unit, who rents it, and what they owe.',
    filters: ['property'],
  },
  {
    name: 'aging',
    label: 'Delinquency and Aging',
    about: 'Who owes money, and for how long.',
    filters: ['asOf'],
  },
  {
    name: 'general_ledger',
    label: 'General Ledger',
    about: 'Every posted line, by account.',
    filters: ['dates', 'property', 'account'],
  },
  {
    name: 'deposit_register',
    label: 'Security Deposit Register',
    about: 'The deposits held for each lease.',
    filters: ['asOf'],
  },
  {
    name: 'budget_vs_actual',
    label: 'Budget vs. Actual',
    about: 'Your budget against what really happened this year.',
    filters: ['year', 'property'],
  },
  {
    name: 'form_1099',
    label: '1099 Summary',
    about: 'Yearly totals for vendors and owners, to review with your accountant.',
    filters: ['year'],
  },
  {
    name: 'trust_reconciliation',
    label: 'Three-Way Trust Reconciliation',
    about: 'The bank, the trust account on the books, and the deposits held.',
    filters: ['asOf'],
  },
];

function showCell(value: string, column: ReportColumnOut): string {
  if (value === '') return '';
  if (column.kind === 'money' && /^-?\d/.test(value)) return formatMoney(value);
  if (column.kind === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDate(value);
  return value;
}

export function ReportsPage() {
  const [name, setName] = useState<ReportName>('income_statement');
  const today = todayIso();
  const [start, setStart] = useState(`${today.slice(0, 7)}-01`);
  const [end, setEnd] = useState(today);
  const [propertyId, setPropertyId] = useState('');
  const [basis, setBasis] = useState<'accrual' | 'cash'>('accrual');
  const [account, setAccount] = useState('');
  const [year, setYear] = useState(today.slice(0, 4));
  const paging = useTablePage();
  const properties = okItems(useListProperties({ page: 1, page_size: 200 }).data);
  const chosen = REPORTS.find((r) => r.name === name) ?? REPORTS[0];
  const uses = (filter: Filter) => chosen.filters.includes(filter);

  const params: GetReportParams = {
    ...(uses('dates') ? { start, end } : {}),
    ...(uses('asOf') ? { end } : {}),
    ...(uses('property') && propertyId ? { property_id: Number(propertyId) } : {}),
    ...(uses('basis') ? { basis } : {}),
    ...(uses('account') && account.trim() ? { account_code: account.trim() } : {}),
    ...(uses('year') ? { year: Number(year) } : {}),
  };
  const query = useGetReport(name, params);
  const report = okData(query.data);

  const columns = useMemo<DataColumn<ReportRowOut & { key: number }>[]>(
    () =>
      (report?.columns ?? []).map((column, index) => ({
        label: column.label,
        align: column.kind === 'money' || column.kind === 'number' ? 'right' : 'left',
        render: (row) => (
          <Box component="span" sx={{ fontWeight: row.emphasis ? 700 : undefined }}>
            {showCell(row.cells[index] ?? '', column)}
          </Box>
        ),
      })),
    [report],
  );
  const rows = (report?.rows ?? []).map((row, key) => ({ ...row, key }));
  const showing = rows.slice(paging.page * paging.pageSize, (paging.page + 1) * paging.pageSize);

  const dateField = (label: string, value: string, set: (next: string) => void) => (
    <TextField
      size="small"
      type="date"
      label={label}
      value={value}
      slotProps={{ inputLabel: { shrink: true } }}
      onChange={(e) => {
        set(e.target.value);
        paging.setPage(0);
      }}
    />
  );

  return (
    <Stack spacing={2}>
      <div>
        <Typography variant="h5">Reports</Typography>
        <Typography color="text.secondary">{chosen.about}</Typography>
      </div>
      <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="Report"
          value={name}
          sx={{ minWidth: 280 }}
          onChange={(e) => {
            setName(e.target.value as ReportName);
            paging.setPage(0);
          }}
        >
          {REPORTS.map((r) => (
            <MenuItem key={r.name} value={r.name}>
              {r.label}
            </MenuItem>
          ))}
        </TextField>
        {uses('dates') && dateField('From', start, setStart)}
        {uses('dates') && dateField('To', end, setEnd)}
        {uses('asOf') && dateField('As Of', end, setEnd)}
        {uses('year') && (
          <TextField
            size="small"
            type="number"
            label="Year"
            value={year}
            sx={{ width: 110 }}
            onChange={(e) => setYear(e.target.value)}
          />
        )}
        {uses('property') && (
          <TextField
            select
            size="small"
            label="Property"
            value={propertyId}
            sx={{ minWidth: 200 }}
            slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
            onChange={(e) => {
              setPropertyId(e.target.value);
              paging.setPage(0);
            }}
          >
            <MenuItem value="">All Properties</MenuItem>
            {properties.map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        {uses('basis') && (
          <TextField
            select
            size="small"
            label="Basis"
            value={basis}
            sx={{ minWidth: 120 }}
            onChange={(e) => setBasis(e.target.value as 'accrual' | 'cash')}
          >
            <MenuItem value="accrual">Accrual</MenuItem>
            <MenuItem value="cash">Cash</MenuItem>
          </TextField>
        )}
        {uses('account') && (
          <TextField
            size="small"
            label="Account Code"
            value={account}
            sx={{ width: 130 }}
            onChange={(e) => {
              setAccount(e.target.value);
              paging.setPage(0);
            }}
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          variant="outlined"
          href={getExportReportUrl(name, { ...params, file_format: 'csv' })}
        >
          Export CSV
        </Button>
        <Button
          size="small"
          variant="outlined"
          href={getExportReportUrl(name, { ...params, file_format: 'pdf' })}
        >
          Export PDF
        </Button>
      </Stack>
      {query.isError && <Alert severity="error">This report couldn't be loaded.</Alert>}
      {query.isLoading && <CircularProgress aria-label="Loading" />}
      {report && (
        <>
          <div>
            <Typography variant="h6">{report.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {report.subtitle}
            </Typography>
          </div>
          <DataTable columns={columns} rows={showing} rowKey={(row) => row.key} />
          <TablePager paging={paging} total={rows.length} />
          {report.note && <Alert severity="info">{report.note}</Alert>}
        </>
      )}
    </Stack>
  );
}
